import { useEffect, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import type { MessageThread } from '@swiss-magic/shared-types';
import { Button } from '@/components/ui/button';
import { MessageList } from '@/components/messaging/MessageList';
import { MessageComposer } from '@/components/messaging/MessageComposer';
import {
  broadcastDirectorMessage,
  getDirectorMessageThread,
  markDirectorThreadRead,
  sendDirectorMessage,
} from '@/lib/api/messages';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast, showSuccessToast } from '@/lib/toast';

function Composer({ onSend }: { onSend: (body: string) => Promise<void> }) {
  const { t } = useTranslation('director');
  return (
    <MessageComposer
      onSend={onSend}
      labels={{
        placeholder: t('messages.thread.placeholder'),
        send: t('messages.thread.send'),
        sending: t('messages.thread.sending'),
        sendError: t('messages.thread.send_error'),
      }}
    />
  );
}

function ThreadShell({
  tournamentId,
  title,
  children,
}: {
  tournamentId: string;
  title: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  return (
    <div data-testid="message-thread-page" className="flex flex-1 flex-col">
      <div className="flex items-center gap-2 py-3">
        <Button
          variant="ghost"
          size="sm"
          data-testid="message-thread-back"
          onClick={() => navigate(`/tournaments/${tournamentId}/messages`)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('messages.thread.back')}
        </Button>
        <h2 className="truncate text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ComposeView({
  tournamentId,
  playerUserIds,
  recipientNames,
}: {
  tournamentId: string;
  playerUserIds: string[];
  recipientNames: Record<string, string>;
}) {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const toLine = playerUserIds
    .map((id) => recipientNames[id])
    .filter(Boolean)
    .join(', ');
  return (
    <ThreadShell tournamentId={tournamentId} title={t('messages.thread.new_title')}>
      {toLine && (
        <p className="px-1 pb-2 text-sm text-muted-foreground">
          {t('messages.compose.to', { names: toLine })}
        </p>
      )}
      <MessageList messages={[]} myRole="director" />
      <Composer
        onSend={async (body) => {
          if (playerUserIds.length === 1) {
            const msg = await sendDirectorMessage(
              tournamentId,
              playerUserIds[0],
              body,
            );
            navigate(`/tournaments/${tournamentId}/messages/${msg.threadId}`, {
              replace: true,
            });
            return;
          }
          const { sent } = await broadcastDirectorMessage(
            tournamentId,
            playerUserIds,
            body,
          );
          showSuccessToast(t('messages.sent_count', { count: sent }));
          navigate(`/tournaments/${tournamentId}/messages`, { replace: true });
        }}
      />
    </ThreadShell>
  );
}

function ThreadView({
  tournamentId,
  threadId,
}: {
  tournamentId: string;
  threadId: string;
}) {
  const { t } = useTranslation('director');
  const { data, message, refetch } = usePolledData<MessageThread>(
    () =>
      getDirectorMessageThread(tournamentId, threadId).catch((err: unknown) => {
        throw err instanceof ApiError ? new Error(err.userMessage) : err;
      }),
    [tournamentId, threadId],
  );
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  const markedRef = useRef(false);
  useEffect(() => {
    if (!markedRef.current && data) {
      markedRef.current = true;
      markDirectorThreadRead(tournamentId, threadId).catch(() => {});
    }
  }, [data, tournamentId, threadId]);

  const title = data?.thread.playerName ?? t('messages.thread.loading');

  return (
    <ThreadShell tournamentId={tournamentId} title={title}>
      <MessageList messages={data?.messages ?? []} myRole="director" />
      <Composer
        onSend={async (body) => {
          if (!data) return;
          await sendDirectorMessage(tournamentId, data.thread.playerUserId, body);
          refetch();
        }}
      />
    </ThreadShell>
  );
}

export function DirectorMessageThreadPage() {
  const { id: tournamentId, threadId } = useParams<{
    id: string;
    threadId: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  if (!tournamentId) return null;

  if (!threadId) {
    const playerUserIds = searchParams.getAll('playerUserId');
    if (playerUserIds.length === 0) {
      return <ThreadShell tournamentId={tournamentId} title="">{null}</ThreadShell>;
    }
    const recipientNames =
      (location.state as { recipientNames?: Record<string, string> } | null)
        ?.recipientNames ?? {};
    return (
      <ComposeView
        tournamentId={tournamentId}
        playerUserIds={playerUserIds}
        recipientNames={recipientNames}
      />
    );
  }
  return <ThreadView tournamentId={tournamentId} threadId={threadId} />;
}
